import Config from 'config/settings';
import { device } from 'utils/device';
import HomeFieldScreensPixelDataConfig from 'app/views/home/webgl/particles/config/HomeFieldScreensPixelDataConfig';
import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_PHYSICS,
	BEHAVIOUR_REPELL,
	FLAG_ACTIVE,
	REPELL_PUSH,
	VEC_X,
	VEC_Y,
	writeVectorConfig
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { GROUP_SPACES } from 'app/views/home/webgl/particlesOptimised/departments/spaces/SpacesContext';

import type { SpacesContext } from 'app/views/home/webgl/particlesOptimised/departments/spaces/SpacesContext';
import type { DepartmentPhase } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';

/**
 * PixelImagesLadyPhase — FORK of SpacesDepartment._pixelImageLady /
 * _pixelImages + the pixel branch of SpacesParticleProcessor.
 *
 * VISUAL: particles fly (eased physics lerps, easeOutBack) onto the pixel
 * grid of the "lady" JSON image inside the box, breathing softly at
 * per-pixel radius scale in additive blend; on arrival each dot arms a PUSH
 * repell and a whoozy vector drift. Over the next seconds (1s..41s
 * randomised counters) dots let go and fall away below their pixel while
 * fading out.
 *
 * The lady item is HomeFieldScreensPixelDataConfig.getList()[0] —
 * synchronous JSON pixel data (HomeParticleJson.toPixelStyleData); no image
 * loads are involved on the live list.
 */
export class PixelImagesLadyPhase implements DepartmentPhase {
	public readonly name = 'PixelImagesLady';

	private _ctx: SpacesContext;
	private _handlerId = 0;

	//The per-lane pixel target (the fall-away lerp reuses it), lane - start:
	private _pixelX: Float32Array;
	private _pixelY: Float32Array;

	constructor(ctx: SpacesContext) {
		this._ctx = ctx;
	}

	public init(): void {
		this._pixelX = new Float32Array(this._ctx.range.count);
		this._pixelY = new Float32Array(this._ctx.range.count);
		//ParticlePhysics.onTargetReached closure -> handler id:
		this._handlerId = this._ctx.registerPhysicsHandler((lane: number) => this._pixelTargetReached(lane));
	}

	public start(): void {
		const item = HomeFieldScreensPixelDataConfig.getList()[0];
		const particleAtCount = device.innerWidth < Config.mobileBreakpoint ? 4 : 2;
		this._pixelImages(item, particleAtCount, 0.24, 1.2, 1.2, 0.13, 0.18);
	}

	public tick(): void {
		//All motion is kernel-side; the fall-away counters live in the
		//shared script list (GROUP_SPACES).
	}

	public hide(): void {
		//The pending fall-away counters die with the transition's group
		//cancel; in-flight lerps carry over exactly like the reference
		//(deactivated physics keeps isLerping until a later arm removes it).
	}

	public reset(): void {
		//No persistent phase state.
	}

	/** SpacesDepartment._pixelImages, lane-for-line over the pool. */
	private _pixelImages(
		item: any,
		particleAtCount = 2,
		rscale = 0.18,
		wCorrect = 1.2,
		yCorrect = 1.2,
		indexXP = 0.13,
		indexYP = 0.18
	): void {
		const ctx = this._ctx;
		const core = ctx.core;
		const frame = ctx.getFrameSizes();

		const frameX1 = frame.x1;
		const frameWidth = frame.width;

		const frameY1 = frame.y1;
		const frameHeight = frame.height;

		const indentX = -1 * (frameWidth * indexXP);
		const indentY = -1 * (frameHeight * indexYP);

		let count = 0;
		let pIndexCount = 0;

		for (let i = 0; i < item.pixelData.length; i++) {
			count++;

			if (count >= particleAtCount && pIndexCount < ctx.activeCount) {
				count = 0;

				const lane = ctx.range.start + pIndexCount;
				pIndexCount++;

				ctx.deactivateAll(lane);
				//particlePhysics.reset():
				core.vx[lane] = 0;
				core.vy[lane] = 0;
				core.gravity[lane] = 0;

				//activate():
				core.stateFlags[lane] |= FLAG_ACTIVE;
				//sprite.blendMode = 'add':
				core.blendAdd[lane] = 1;
				Math.random(); //bare roll kept (reference dead ternary)
				const baseScale = item.pixelData[i].radius * rscale;
				//hardSetScale(baseScale):
				core.scale[lane] = baseScale;
				core.tScale[lane] = baseScale;
				core.tScaleSaved[lane] = baseScale;
				//mouseParallax.active = false — bit already off after the reset.
				//alpha = targetAlpha = 0.8 + rand * 0.2:
				core.alpha[lane] = core.tAlpha[lane] = 0.8 + Math.random() * 0.2;

				const x = frameX1 + indentX + item.pixelData[i].x * (frameWidth * wCorrect);
				const y = frameY1 + indentY + item.pixelData[i].y * (frameHeight * yCorrect);
				this._pixelX[lane - ctx.range.start] = x;
				this._pixelY[lane - ctx.range.start] = y;

				//setVectorStyleMovement(false):
				writeVectorConfig(core, lane, VEC_X, false);
				writeVectorConfig(core, lane, VEC_Y, false);
				//particlePhysics.active = true + the ease-4 flight in:
				core.behaviourMask[lane] |= BEHAVIOUR_PHYSICS;
				ctx.lerpToPosition(lane, x, y, 900 + Math.random() * 1500, 4);
				ctx.physHandler[lane - ctx.range.start] = this._handlerId;

				//repell config written but INACTIVE until the target is reached:
				core.behaviourMask[lane] &= ~BEHAVIOUR_REPELL;
				core.repellType[lane] = REPELL_PUSH;
				core.repellRadius[lane] = 150;
				core.repellPower[lane] = 30 + Math.random() * 100;

				//counter: the randomised fall-away (1s..41s):
				ctx.deps.scripts.add(
					lane,
					(1000 + Math.random() * 40000) / 16,
					(l: number) => {
						writeVectorConfig(core, l, VEC_X, false);
						writeVectorConfig(core, l, VEC_Y, false);
						core.behaviourMask[l] |= BEHAVIOUR_PHYSICS;
						core.easeAlpha[l] = 50;
						core.tAlpha[l] = 0;
						ctx.lerpToPosition(
							l,
							this._pixelX[l - ctx.range.start],
							this._pixelY[l - ctx.range.start] - Math.random() * frameHeight,
							800 + Math.random() * 1500
						);
					},
					false,
					GROUP_SPACES
				);

				//startBreathing(bs, 0.01*bs, 0.4*bs, [-1,1], rand*0.4*bs, ±1) —
				//argument order kept for the RNG stream:
				const startChangeValue = Math.random() * 0.4 * baseScale;
				const startDirection = Math.random() < 0.5 ? -1 : 1;
				core.breathBase[lane] = baseScale;
				core.breathChange[lane] = 0.01 * baseScale;
				core.breathMax[lane] = 0.4 * baseScale;
				core.breathAdd[lane] = startChangeValue * startDirection;
				core.breathDir[lane] = startDirection;
				core.behaviourMask[lane] |= BEHAVIOUR_BREATHING;

				ctx.setEaseXY(lane, 1, 1);
			}
		}

		//The slice(pIndexCount) leftovers hide:
		for (let n = pIndexCount; n < ctx.activeCount; n++) {
			ctx.hideLane(ctx.range.start + n);
		}
	}

	/** The pixel onTargetReached: repell on, whoozy drift on, physics off.
	 *  Kept registered after firing (the reference never clears it — the
	 *  fall-away lerp's completion re-runs this, like the closure). */
	private _pixelTargetReached(lane: number): void {
		const core = this._ctx.core;
		core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
		writeVectorConfig(core, lane, VEC_X, true);
		writeVectorConfig(core, lane, VEC_Y, true);
		core.behaviourMask[lane] &= ~BEHAVIOUR_PHYSICS;
	}
}
